<template>
  <div class="all-events-page">
    <section class="events-hero-section">
      <div class="container">
        <h1 class="events-hero-title">All Events</h1>
        <p class="events-hero-subtitle">Explore upcoming and past franchise events</p>
      </div>
    </section>
    <section class="events-list-section">
      <div class="container">
        <div v-if="eventStore.loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2">Loading events...</p>
        </div>
        <div v-else-if="eventStore.error" class="alert alert-warning text-center my-5" role="alert">
          <h4 class="alert-heading">Unable to load events</h4>
          <p>{{ eventStore.error }}</p>
          <button @click="loadEvents" class="btn btn-primary">Try Again</button>
        </div>
        <div v-else-if="paginatedEvents.length === 0" class="text-center py-5">
          <h4>No events available</h4>
          <p>Check back later for new events.</p>
        </div>
        <div v-else class="events-grid">
          <EventCard 
            v-for="event in paginatedEvents" 
            :key="event.id" 
            :event="event" 
            class="event-card-grid"
          />
        </div>
        <div v-if="totalPages > 1" class="pagination-container">
          <div class="pagination">
            <button 
              @click="currentPage = 1" 
              :disabled="currentPage === 1"
              class="pagination-btn"
            >
              <i class="fa fa-angle-double-left"></i>
            </button>
            <button 
              @click="currentPage--" 
              :disabled="currentPage === 1"
              class="pagination-btn"
            >
              <i class="fa fa-angle-left"></i>
            </button>
            <div class="page-numbers">
              <button 
                v-for="page in visiblePages" 
                :key="page"
                @click="currentPage = page"
                :class="['page-btn', { active: currentPage === page }]"
              >
                {{ page }}
              </button>
            </div>
            <button 
              @click="currentPage++" 
              :disabled="currentPage === totalPages"
              class="pagination-btn"
            >
              <i class="fa fa-angle-right"></i>
            </button>
            <button 
              @click="currentPage = totalPages" 
              :disabled="currentPage === totalPages"
              class="pagination-btn"
            >
              <i class="fa fa-angle-double-right"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useEventStore } from '../stores/eventStore'
import EventCard from '../components/EventCard.vue'

const eventStore = useEventStore()
const currentPage = ref(1)
const itemsPerPage = ref(9)

const loadEvents = async () => {
  await eventStore.fetchEvents()
}

onMounted(() => {
  if (eventStore.events.length === 0) {
    loadEvents()
  }
})

const sortedEvents = computed(() => {
  return [...eventStore.events].sort((a, b) => new Date(b.date) - new Date(a.date))
})

const totalPages = computed(() => Math.ceil(sortedEvents.value.length / itemsPerPage.value))
const paginatedEvents = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  return sortedEvents.value.slice(start, start + itemsPerPage.value)
})
const visiblePages = computed(() => {
  const pages = []
  const maxVisible = 5
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(totalPages.value, start + maxVisible - 1)
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})
</script>

<style scoped>
.all-events-page {
  min-height: 100vh;
  background: #f8fafc;
}
.events-hero-section {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  padding: 64px 0 40px 0;
  color: #fff;
  text-align: center;
}
.events-hero-title {
  font-size: 2.8rem;
  font-weight: 800;
  margin-bottom: 12px;
  letter-spacing: -0.5px;
}
.events-hero-subtitle {
  font-size: 1.15rem;
  opacity: 0.95;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}
.events-list-section {
  padding: 48px 0 64px 0;
}
.events-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-bottom: 40px;
}
@media (max-width: 1100px) {
  .events-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }
}
@media (max-width: 700px) {
  .events-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }
}
.event-card-grid {
  height: 100%;
  display: flex;
}
.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}
.pagination {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pagination-btn {
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6b7280;
}
.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pagination-btn:not(:disabled):hover {
  border-color: #2563eb;
  color: #2563eb;
}
.page-numbers {
  display: flex;
  gap: 4px;
}
.page-btn {
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6b7280;
  min-width: 44px;
}
.page-btn.active {
  background: #2563eb;
  border-color: #2563eb;
  color: white;
}
.page-btn:hover:not(.active) {
  border-color: #2563eb;
  color: #2563eb;
}
</style> 